/* eslint-disable @typescript-eslint/ban-ts-comment */
import { FC, useEffect, useMemo, useState } from 'react';
import { useHistory } from 'react-router-dom';
import { getStatusStock } from '../../../service/dashboard/Dashboard';

type Props = {
  init_date?: string;
  end_date?: string;
};

type DetailItem = {
  status: string;
  count: number;
  percentage: number;
};

type DataSends = {
  total_stock: number;
  details: DetailItem[];
};

const MovInventory: FC<Props> = ({ init_date, end_date }) => {
  const history = useHistory();
  const [statusFilter, setStatusFilter] = useState<number>(0);
  const [dataSends, setDataSends] = useState<DataSends | null>(null);

  const fetchPost = async () => {
    const filters: any = {
      type_stock: statusFilter,
      init_date: init_date || '',
      end_date: end_date || '',
    };
    try {
      const sends = await getStatusStock(filters);
      if (sends) setDataSends(sends);
    } catch (error) {
      console.error('Error al obtener datos:', error);
    }
  };

  useEffect(() => {
    fetchPost();
  }, [statusFilter, init_date, end_date]);

  const details = dataSends?.details ?? [];
  const totalInventory = dataSends?.total_stock ?? 0;

  // Mapeo de colores por status
  const colorMap: Record<string, string> = {
    Disponibles: '#11CC2A',
    Activadas: '#7B3FF2',
    Portadas: '#FFBB00',
    Enviadas: '#009EF7',
    Asignadas: '#CACEE4',
    Otros: '#EF4444',
  };

  // Orden deseado para mostrar los segmentos
  const statusOrder = [
    'Disponibles',
    'Activadas',
    'Portadas',
    'Enviadas',
    'Asignadas',
    'Otros',
  ];

  const segments = useMemo(() => {
    return statusOrder.map((status) => {
      const item = details.find((d) => d.status === status);
      return {
        key: status.toLowerCase(),
        label: status,
        count: item?.count ?? 0,
        percent: item?.percentage ?? 0,
        color: colorMap[status] || '#999999',
      };
    });
  }, [details]);

  const dropdownRender = () => {
    const filters = [
      { label: 'Todo', value: 0 },
      { label: 'SIM', value: 1 },
      { label: 'E-SIM', value: 2 },
    ];

    return (
      <div className="d-flex justify-content-end flex-shrink-0">
        <button
          type="button"
          data-kt-menu="true"
          className="btn btn-sm btn-icon btn-white btn-active-light"
          data-kt-menu-trigger="click"
          data-kt-menu-placement="bottom-end"
          tabIndex={0}
          aria-label="Abrir menú"
        >
          <i className="bi bi-three-dots-vertical fs-3"></i>
        </button>

        <div
          className="menu menu-sub menu-sub-dropdown menu-column menu-rounded menu-gray-600 menu-state-bg-light-primary fw-bold fs-7 w-150px py-4"
          data-kt-menu="true"
        >
          {filters.map((filter) => (
            <div className="menu-item px-3" key={filter.value}>
              <button
                type="button"
                className={`menu-link btn btn-color-muted btn-active btn-active-light-primary px-3 ${
                  statusFilter === filter.value ? 'active' : ''
                }`}
                onClick={() => setStatusFilter(filter.value)}
                style={{
                  width: '100%',
                  textAlign: 'left',
                  background: 'none',
                  border: 'none',
                  padding: 0,
                }}
              >
                {filter.label}
              </button>
            </div>
          ))}
        </div>
      </div>
    );
  };

  return (
    <div className="card card-flush h-100">
      <div className="card-header pt-6 pb-3 d-flex justify-content-between align-items-start">
        <div className="card-title d-flex flex-column m-0">
          <span className="fs-2 fw-bolder text-gray-900">
            Monitoreo de inventario
          </span>
          <span className="text-gray-500 fw-semibold fs-6">
            Monitoreo de tarjetas SIM
          </span>
        </div>
      </div>

      <div className="card-body pt-2 d-flex flex-column">
        <div className="mb-6">
          <div className="fw-bolder text-gray-900 fs-2">
            {totalInventory.toLocaleString('es-MX')}
          </div>
          <div className="text-gray-500 fw-semibold fs-6">
            Stock actual de inventario
          </div>
        </div>

        <div className="mb-8">
          <div
            className="d-flex overflow-hidden"
            style={{
              height: 18,
              borderRadius: 999,
              background: '#EEF0F6',
              border: '1px solid #D7DBEA',
            }}
          >
            {segments.map((s, idx) => {
              const isFirst = idx === 0;
              const isLast = idx === segments.length - 1;

              const w = s.percent > 0 ? Math.max(s.percent, 2) : 0;

              return (
                <div
                  key={s.key}
                  style={{
                    width: `${w}%`,
                    background: s.color,
                    borderTopLeftRadius: isFirst ? 999 : 0,
                    borderBottomLeftRadius: isFirst ? 999 : 0,
                    borderTopRightRadius: isLast ? 999 : 0,
                    borderBottomRightRadius: isLast ? 999 : 0,
                  }}
                  title={`${s.label}: ${s.count} (${s.percent.toFixed(1)}%)`}
                />
              );
            })}
          </div>
        </div>

        <div className="d-flex flex-column gap-4 mt-4">
          {segments.map((s) => (
            <div key={s.key} className="d-flex align-items-center">
              <span
                style={{
                  width: 8,
                  height: 20,
                  borderRadius: 4,
                  background: s.color,
                  display: 'inline-block',
                  marginRight: 14,
                }}
              />
              <div className="flex-grow-1 fw-bold text-gray-600">{s.label}</div>

              <div
                className="fw-bolder text-gray-800"
                style={{ width: 80, textAlign: 'right' }}
              >
                {s.count} Unid.
              </div>

              <div
                className="fw-bold text-gray-500"
                style={{ width: 70, textAlign: 'right' }}
              >
                {s.percent.toFixed(1)} %
              </div>
            </div>
          ))}
        </div>

        <div className="d-flex justify-content-center mt-auto pt-5">
          <button
            className="btn btn-sm btn-light-primary fs-6"
            onClick={() => history.push('/inventario/producto')}
          >
            Ver detalles <i className="bi bi-arrow-right ms-2" />
          </button>
        </div>
      </div>
    </div>
  );
};

export { MovInventory };
